import type { ApiResponse, Restaurant, VenueStaff, VenueRole, BusinessHours, TemporaryClosure, CoverMediaItem } from '@gatheredhq/types';
import { apiFetch, uploadFileAsBase64 } from './client.js';

export async function fetchVenues(): Promise<(Restaurant & { _role?: VenueRole | null; _userPermissions?: string[] | null })[]> {
  const res = await apiFetch<ApiResponse<{ venues: (Restaurant & { _role?: VenueRole | null; _userPermissions?: string[] | null })[] }>>('/api/venues');
  return res.data?.venues ?? [];
}

export interface CreateVenuePayload {
  name: string;
  venueType?: string;
  serviceMode?: 'waiter' | 'self_service' | 'hybrid';
  emirate?: string | null;
  address?: string | null;
  phone?: string | null;
  website?: string | null;
  googleMapLink?: string | null;
}

export async function createVenue(payload: CreateVenuePayload): Promise<Restaurant> {
  const res = await apiFetch<ApiResponse<Restaurant>>('/api/venues', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to create venue');
  return res.data;
}

export async function fetchVenueBySlug(slug: string): Promise<Restaurant | null> {
  try {
    const res = await apiFetch<ApiResponse<Restaurant>>(`/api/venues/${slug}`);
    return res.data ?? null;
  } catch {
    return null;
  }
}

export interface UpdateVenuePayload {
  name?: string;
  slug?: string;
  venue_type?: string;
  description?: string | null;
  address?: string | null;
  emirate?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  google_map_link?: string | null;
  google_place_id?: string | null;
  logo_url?: string | null;
  cover_image_url?: string | null;
  cover_media?: CoverMediaItem[] | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  youtube_url?: string | null;
  twitter_url?: string | null;
  open_time?: string | null;
  close_time?: string | null;
  timezone?: string;
  business_hours?: BusinessHours | null;
  temporary_closure?: TemporaryClosure | null;
  is_active?: boolean;
  tips_enabled?: boolean;
  location_ordering_enabled?: boolean;
  location_ordering_label?: string;
  ai_order_assistant_enabled?: boolean;
  sound_alerts_enabled?: boolean;
  service_mode?: string;
  enabled_languages?: string[];
  enabled_currencies?: string[];
  wifi_settings?: { ssid: string; password: string } | null;
  guest_theme_mode?: string;
  live_vibes_enabled?: boolean;
  menu_layout?: 'scroll' | 'grid';
  whatsapp_number?: string | null;
  trade_license_number?: string | null;
  trade_license_url?: string | null;
}

export type SlugAvailabilityReason = 'ok' | 'current' | 'invalid' | 'reserved' | 'taken';

/** Real-time availability check for a custom link (slug). `slug` is the venue's
 *  current slug (identifies the venue); `candidate` is the link being tried. */
export async function checkVenueSlug(
  slug: string,
  candidate: string,
): Promise<{ available: boolean; reason: SlugAvailabilityReason }> {
  const params = new URLSearchParams({ candidate });
  const res = await apiFetch<ApiResponse<{ available: boolean; reason: SlugAvailabilityReason }>>(
    `/api/venues/${slug}/slug-available?${params.toString()}`,
  );
  return res.data ?? { available: false, reason: 'invalid' };
}

export async function uploadVenueImage(
  slug: string,
  imageBase64: string,
  type: 'logo' | 'cover' | 'cover_video' | 'trade_license',
  mimeType: string = 'image/webp',
): Promise<Restaurant> {
  const res = await apiFetch<ApiResponse<Restaurant>>(`/api/venues/${slug}/upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageBase64, mimeType, type }),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to upload image');
  return res.data;
}

/**
 * Upload a single cover-story media item (image or video). Returns its URL + type.
 *
 * @param onProgress Receives 0–100 of bytes uploaded. Server-side resize happens
 * after 100%, so keep a "processing" state until this resolves.
 */
export async function uploadCoverMedia(
  slug: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<CoverMediaItem> {
  const res = await uploadFileAsBase64<ApiResponse<CoverMediaItem>>(
    `/api/venues/${slug}/cover-media`,
    file,
    (media) => ({ media, mimeType: file.type }),
    onProgress,
  );
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to upload media');
  return res.data;
}

export async function updateVenue(slug: string, payload: UpdateVenuePayload): Promise<Restaurant> {
  const res = await apiFetch<ApiResponse<Restaurant>>(`/api/venues/${slug}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to update venue');
  return res.data;
}

// ── Google Places linking (venue settings) ────────────────────────────────────

export interface PlacePrediction {
  placeId: string;
  primaryText: string;   // business name
  secondaryText: string; // address / locality
}

/** Autocomplete Google Places by business name. `token` groups a search session for billing. */
export async function searchPlaces(slug: string, q: string, token?: string): Promise<PlacePrediction[]> {
  const params = new URLSearchParams({ q });
  if (token) params.set('token', token);
  const res = await apiFetch<ApiResponse<PlacePrediction[]>>(`/api/venues/${slug}/place-search?${params.toString()}`);
  return res.data ?? [];
}

/** Link a picked place: server captures the place id, address, maps link, and seeds the rating. */
export async function linkPlace(slug: string, placeId: string, sessionToken?: string): Promise<Restaurant> {
  const res = await apiFetch<ApiResponse<Restaurant>>(`/api/venues/${slug}/link-place`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ placeId, sessionToken }),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to link place');
  return res.data;
}

export async function fetchVenueStaff(slug: string): Promise<{ staff: VenueStaff[]; orgStaffCount: number }> {
  const res = await apiFetch<ApiResponse<{ staff: VenueStaff[]; orgStaffCount: number }>>(`/api/venues/${slug}/staff`);
  if (!res.success) throw new Error(res.error?.message ?? 'Failed to fetch staff');
  return { staff: res.data?.staff ?? [], orgStaffCount: res.data?.orgStaffCount ?? 0 };
}

export interface CreateStaffPayload {
  name: string;
  staff_id: string;
  pin: string;
  role: 'kitchen_staff' | 'bar_staff' | 'waiter' | 'cashier' | 'delivery_staff';
  email?: string | null;
  phone?: string | null;
  shift: string;
  preparation_assignment?: 'kitchen' | 'bar' | 'both' | null;
}

export interface UpdateStaffPayload {
  name?: string;
  pin?: string;
  role?: 'kitchen_staff' | 'bar_staff' | 'waiter' | 'cashier' | 'delivery_staff';
  email?: string | null;
  phone?: string | null;
  shift?: string;
  preparation_assignment?: 'kitchen' | 'bar' | 'both' | null;
  is_active?: boolean;
}

export async function createVenueStaff(slug: string, payload: CreateStaffPayload): Promise<VenueStaff> {
  const res = await apiFetch<ApiResponse<VenueStaff>>(`/api/venues/${slug}/staff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to add staff');
  return res.data;
}

export async function updateVenueStaff(slug: string, staffId: string, payload: UpdateStaffPayload): Promise<VenueStaff> {
  const res = await apiFetch<ApiResponse<VenueStaff>>(`/api/venues/${slug}/staff/${staffId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to update staff');
  return res.data;
}

export async function sendStaffCredentials(slug: string, staffId: string): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/staff/${staffId}/send-credentials`, { method: 'POST' });
}

export async function deleteVenueStaff(slug: string, staffId: string): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/staff/${staffId}`, { method: 'DELETE' });
}

// ── Waiter Table Assignments ──────────────────────────────────────────────────

export async function getWaiterTableAssignments(slug: string, staffId: string): Promise<string[]> {
  const res = await apiFetch<{ success: boolean; data?: string[] }>(`/api/venues/${slug}/staff/${staffId}/table-assignments`);
  return res.data ?? [];
}

export async function updateWaiterTableAssignments(slug: string, staffId: string, tableIds: string[]): Promise<void> {
  await apiFetch(`/api/venues/${slug}/staff/${staffId}/table-assignments`, {
    method: 'PUT',
    body: JSON.stringify({ tableIds }),
  });
}

// ── Stripe Connect ───────────────────────────────────────────────────────────

export async function connectOnboard(slug: string, returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
  const res = await apiFetch<{ success: boolean; data?: { url: string } }>(`/api/venues/${slug}/connect/onboard`, {
    method: 'POST',
    body: JSON.stringify({ returnUrl, refreshUrl }),
  });
  if (!res.data?.url) throw new Error('Failed to start onboarding');
  return { url: res.data.url };
}

export async function connectStatus(slug: string): Promise<{
  stripeAccountId: string | null;
  onboardingComplete: boolean;
  payoutsEnabled: boolean;
}> {
  const res = await apiFetch<{ success: boolean; data?: { stripeAccountId: string | null; onboardingComplete: boolean; payoutsEnabled: boolean } }>(
    `/api/venues/${slug}/connect/status`,
  );
  return res.data ?? { stripeAccountId: null, onboardingComplete: false, payoutsEnabled: false };
}

export async function connectDashboard(slug: string): Promise<{ url: string }> {
  const res = await apiFetch<{ success: boolean; data?: { url: string } }>(`/api/venues/${slug}/connect/dashboard`, {
    method: 'POST',
  });
  if (!res.data?.url) throw new Error('Failed to get dashboard link');
  return { url: res.data.url };
}

// ── Team Members ──────────────────────────────────────────────────────────────

export interface VenueMember {
  userId: string;
  email: string;
  fullName: string | null;
  role: VenueRole;
  permissions: string[] | null;
  joinedAt: string;
}

export async function fetchVenueMembers(slug: string): Promise<VenueMember[]> {
  const res = await apiFetch<ApiResponse<{ members: Record<string, unknown>[] }>>(`/api/venues/${slug}/members`);
  if (!res.success) throw new Error(res.error?.message ?? 'Failed to fetch members');
  return (res.data?.members ?? []).map((m) => ({
    userId: String(m.user_id ?? m.userId ?? ''),
    email: String(m.email ?? ''),
    fullName: (m.full_name ?? m.fullName ?? null) as string | null,
    role: (m.role ?? 'manager') as VenueRole,
    permissions: Array.isArray(m.permissions) ? m.permissions as string[] : null,
    joinedAt: String(m.created_at ?? m.joinedAt ?? ''),
  }));
}

export async function removeVenueMember(slug: string, userId: string): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/members/${userId}`, { method: 'DELETE' });
}

export async function updateMemberPermissions(slug: string, userId: string, permissions: string[] | null): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/members/${userId}/permissions`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ permissions }),
  });
}

// ── Invites ───────────────────────────────────────────────────────────────────

export interface VenueInvite {
  id: string;
  email: string;
  role: 'manager';
  token: string;
  permissions: string[] | null;
  expiresAt: string;
  createdAt: string;
}

export async function fetchVenueInvites(slug: string): Promise<VenueInvite[]> {
  const res = await apiFetch<ApiResponse<{ invites: Record<string, unknown>[] }>>(`/api/venues/${slug}/invites`);
  if (!res.success) throw new Error(res.error?.message ?? 'Failed to fetch invites');
  return (res.data?.invites ?? []).map((inv) => ({
    id: String(inv.id ?? ''),
    email: String(inv.email ?? ''),
    role: 'manager' as const,
    token: String(inv.token ?? ''),
    permissions: Array.isArray(inv.permissions) ? inv.permissions as string[] : null,
    expiresAt: String(inv.expires_at ?? inv.expiresAt ?? ''),
    createdAt: String(inv.created_at ?? inv.createdAt ?? ''),
  }));
}

export async function createVenueInvite(slug: string, email: string, permissions?: string[]): Promise<VenueInvite> {
  const res = await apiFetch<ApiResponse<VenueInvite>>(`/api/venues/${slug}/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, permissions }),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to send invite');
  return res.data;
}

export async function revokeVenueInvite(slug: string, inviteId: string): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/invites/${inviteId}`, { method: 'DELETE' });
}

export async function resendVenueInvite(slug: string, inviteId: string): Promise<void> {
  await apiFetch<void>(`/api/venues/${slug}/invites/${inviteId}/resend`, { method: 'POST' });
}
